import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Highlight } from './Highlight.js';
export function CommandPalette(props) {
    // Mounted anew on every opening, so the typed text and the active option start clean.
    return props.open ? _jsx(PaletteBody, { ...props }) : null;
}
function PaletteBody({ commands, onClose, filter, onSearch, results = [], onTermChange, labels, }) {
    const [term, setTerm] = useState('');
    const [active, setActive] = useState(0);
    const inputRef = useRef(null);
    const openerRef = useRef(null);
    const listId = useId();
    // Before the browser handles the next key: note who opened the palette, then take the focus,
    // so keys typed right after ⌘K land in the field and Esc can give the focus back.
    useLayoutEffect(() => {
        // StrictMode runs this twice in development: the second time the field already has the focus
        // and must not be taken for the element that opened the palette.
        if (document.activeElement !== inputRef.current)
            openerRef.current = document.activeElement;
        inputRef.current?.focus();
    }, []);
    const matches = useMemo(() => commands.filter((c) => filter(c, term)), [commands, filter, term]);
    const found = term.trim() === '' ? [] : results;
    const searchOption = onSearch && term.trim() !== '';
    const searchAt = matches.length + found.length;
    const total = searchAt + (searchOption ? 1 : 0);
    // Results arrive after the text changes: never point past the end.
    const current = Math.min(active, Math.max(total - 1, 0));
    const close = () => {
        onClose();
        // Give the focus back to whatever opened the palette (docs/05 §9).
        openerRef.current?.focus?.();
    };
    const runAt = (index) => {
        const command = matches[index] ?? found[index - matches.length];
        if (command) {
            close();
            command.run();
            return;
        }
        if (searchOption) {
            onSearch(term);
            close();
        }
    };
    const onKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            close();
            return;
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (total === 0)
                return;
            const step = event.key === 'ArrowDown' ? 1 : -1;
            setActive((current + step + total) % total);
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            runAt(current);
        }
    };
    // Options grouped as listbox > group > option (ARIA), in the order the commands come.
    const groups = [];
    matches.forEach((command, position) => {
        const last = groups[groups.length - 1];
        if (last?.name === command.group)
            last.items.push({ command, position });
        else
            groups.push({ name: command.group, items: [{ command, position }] });
    });
    return (_jsxs("div", { className: "palette", role: "dialog", "aria-modal": "true", "aria-label": labels.title, children: [_jsxs("div", { className: "palette__panel", children: [_jsx("input", { ref: inputRef, type: "text", className: "palette__input", role: "combobox", "aria-expanded": "true", "aria-controls": listId, "aria-activedescendant": total > 0 ? `${listId}-option-${current}` : undefined, "aria-label": labels.title, placeholder: labels.placeholder, value: term, onChange: (event) => {
                            setTerm(event.target.value);
                            setActive(0);
                            onTermChange?.(event.target.value);
                        }, onKeyDown: onKeyDown }), _jsxs("div", { className: "palette__list", id: listId, role: "listbox", "aria-label": labels.title, children: [groups.map((group, groupIndex) => {
                                const headingId = `${listId}-group-${groupIndex}`;
                                return (_jsxs("div", { role: "group", "aria-labelledby": headingId, children: [_jsx("div", { id: headingId, className: "palette__group", role: "presentation", children: group.name }), group.items.map(({ command, position }) => (_jsxs("div", { role: "option", id: `${listId}-option-${position}`, "aria-selected": position === current, className: "palette__option", "data-active": position === current, onMouseEnter: () => setActive(position), onClick: () => runAt(position), children: [_jsx("span", { children: command.title }), command.hint ? _jsx("kbd", { className: "palette__hint", children: command.hint }) : null] }, command.id)))] }, group.name));
                            }), found.length > 0 ? (_jsxs("div", { role: "group", "aria-labelledby": `${listId}-results`, children: [_jsx("div", { id: `${listId}-results`, className: "palette__group", role: "presentation", children: labels.results }), found.map((result, index) => {
                                        const position = matches.length + index;
                                        return (_jsxs("div", { role: "option", id: `${listId}-option-${position}`, "aria-selected": position === current, className: "palette__option palette__result", "data-active": position === current, onMouseEnter: () => setActive(position), onClick: () => runAt(position), children: [_jsxs("span", { className: "palette__result-text", children: [_jsx("span", { className: "palette__result-title", children: result.title }), _jsx(Highlight, { className: "palette__snippet", parts: result.parts })] }), _jsx("span", { className: "palette__hint", children: result.kind })] }, result.id));
                                    })] })) : null, searchOption ? (_jsx("div", { role: "option", id: `${listId}-option-${searchAt}`, "aria-selected": searchAt === current, className: "palette__option", "data-active": searchAt === current, onMouseEnter: () => setActive(searchAt), onClick: () => runAt(searchAt), children: labels.search(term) })) : null, total === 0 ? (_jsx("div", { className: "palette__empty", role: "presentation", children: labels.empty })) : null] })] }), _jsx("button", { type: "button", className: "palette__scrim", "aria-label": labels.close, onClick: close })] }));
}
//# sourceMappingURL=CommandPalette.js.map